import { IPrismicDocument } from '@monofront/types';
import { linkResolver } from '@utils/prismic';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { useAuthentication } from '@context/businessContainer';
import { useGoogleShoppingContext } from '@context/googleShopping';

enum Rules {
  asEmail = 'asEmail',
  isAuthenticated = 'isAuthenticated',
  isFromGoogleShopping = 'isFromGoogleShopping',
  isFromSocialMedia = 'isFromSocialMedia',
  isMember = 'isMember',
  redirectTo = 'redirectTo',
}

type IRules = Partial<Record<Rules, boolean>>;

export interface IPrismicRules {
  primary: {
    url: IPrismicDocument;
    items: {
      rule_name: Rules;
      value: boolean;
    }[];
  };
}

const formatRules: (IPrismicRules) => Record<string, IRules> = (rules) =>
  rules.reduce(
    (acc, rule) => ({
      ...acc,
      [linkResolver(rule.primary.url)]: rule.items.reduce(
        (itemAcc, item) => ({
          ...itemAcc,
          [item.rule_name]: item.value,
        }),
        {},
      ),
    }),
    {},
  );

const testRules = (userState: IRules, routeRules: IRules) =>
  !Object.keys(routeRules).find(
    (ruleId) => Boolean(userState[ruleId]) !== Boolean(routeRules[ruleId]),
  );

const useRedirections = (rules: IPrismicRules | null) => {
  const router = useRouter();
  const { authUser, isFromSocialMedia, isMember, isLoading } =
    useAuthentication();
  const { isFromGoogleShopping } = useGoogleShoppingContext();
  const hasCheckedRules = useRef(false);

  useEffect(() => {
    if (
      rules &&
      !isLoading &&
      typeof isFromGoogleShopping === 'boolean' &&
      hasCheckedRules.current === false
    ) {
      hasCheckedRules.current = true;
      const routesRules = formatRules(rules);

      const userState: IRules = {
        isMember,
        isFromGoogleShopping,
        isAuthenticated: Boolean(authUser),
        asEmail: Boolean(authUser?.email),
        isFromSocialMedia,
      };

      if (
        routesRules[router.asPath] &&
        !testRules(userState, routesRules[router.asPath])
      ) {
        const routeToPush = Object.keys(routesRules).find((route) =>
          testRules(userState, routesRules[route]),
        );

        routeToPush && router.push(routeToPush);
      }
    }
  }, [isLoading, isFromGoogleShopping, isMember, router, authUser, rules]);
};

export default useRedirections;
